import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plane, RefreshCw, PlaneTakeoff, PlaneLanding } from "lucide-react";
import { toast } from "sonner";

const EMPTY_CONN = {
  pnr: "", flight_number: "", origin: "", destination: "", datetime_at: "", airport: "",
};

const EMPTY = {
  passenger_ids: [], airline: "", pnr: "", origin: "", destination: "",
  departure_at: "", return_at: "", flight_number: "",
  price: 0, cost: 0, notes: "",
  ticket_url: "",
  order_number: "",
  trip_type: "one_way",
  outbound_connection: null,
  return_connection: null,
  return_origin: "", return_destination: "", return_flight_number: "",
  return_pnr: "", return_airline: "",
};

function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function ConnectionFields({ value, onChange, prefix }) {
  const v = value || EMPTY_CONN;
  const set = (k) => (e) => onChange({ ...v, [k]: e.target.value });
  const setU = (k) => (e) => onChange({ ...v, [k]: e.target.value.toUpperCase() });
  return (
    <div className="rounded-lg border border-blue-100 bg-blue-50/40 p-3 space-y-2.5"
      data-testid={`${prefix}-connection-fields`}>
      <div className="text-xs font-semibold text-[#0b3a82] flex items-center gap-1">
        <RefreshCw className="w-3.5 h-3.5" /> Dados da conexão
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">Origem</Label>
          <Input value={v.origin} onChange={setU("origin")} placeholder="GRU"
            data-testid={`${prefix}-conn-origin`} />
        </div>
        <div>
          <Label className="text-xs">Destino</Label>
          <Input value={v.destination} onChange={setU("destination")} placeholder="MAD"
            data-testid={`${prefix}-conn-destination`} />
        </div>
      </div>
      <div>
        <Label className="text-xs">Data e horário</Label>
        <Input type="datetime-local" value={v.datetime_at} onChange={set("datetime_at")}
          data-testid={`${prefix}-conn-datetime`} />
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <Label className="text-xs">Aeroporto</Label>
          <Input value={v.airport || ""} onChange={set("airport")} placeholder="Aeroporto da conexão"
            data-testid={`${prefix}-conn-airport`} />
        </div>
        <div>
          <Label className="text-xs">Voo</Label>
          <Input value={v.flight_number || ""} onChange={set("flight_number")} placeholder="LA8088"
            data-testid={`${prefix}-conn-flight`} />
        </div>
        <div>
          <Label className="text-xs">Localizador</Label>
          <Input value={v.pnr || ""} onChange={(e) => onChange({ ...v, pnr: e.target.value.toUpperCase() })}
            placeholder="ABC123" data-testid={`${prefix}-conn-pnr`} />
        </div>
      </div>
    </div>
  );
}

export default function EmissionFormDialog({ open, onOpenChange, emission, onSaved, airlines = [] }) {
  const [form, setForm] = useState(EMPTY);
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/clients").then(({ data }) => setClients(data)).catch(() => {});
    setSearch("");
  }, [open]);

  useEffect(() => {
    if (emission) {
      const pids = emission.passenger_ids?.length
        ? emission.passenger_ids
        : emission.client_id ? [emission.client_id] : [];
      const trip = emission.trip_type || (emission.return_at ? "round_trip" : "one_way");
      const outConn = emission.outbound_connection ? {
        ...emission.outbound_connection,
        datetime_at: toLocalInput(emission.outbound_connection.datetime_at),
      } : null;
      const retConn = emission.return_connection ? {
        ...emission.return_connection,
        datetime_at: toLocalInput(emission.return_connection.datetime_at),
      } : null;
      setForm({
        ...EMPTY,
        ...emission,
        passenger_ids: pids,
        cost: emission.cost ?? 0,
        ticket_url: emission.ticket_url || "",
        order_number: emission.order_number || "",
        trip_type: trip,
        outbound_connection: outConn,
        return_connection: retConn,
        departure_at: toLocalInput(emission.departure_at),
        return_at: toLocalInput(emission.return_at),
      });
    } else setForm(EMPTY);
  }, [emission, open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const togglePassenger = (cid) => {
    setForm((f) => {
      const has = f.passenger_ids.includes(cid);
      return { ...f, passenger_ids: has ? f.passenger_ids.filter((x) => x !== cid) : [...f.passenger_ids, cid] };
    });
  };

  const setTripType = (v) => {
    setForm((f) => ({
      ...f,
      trip_type: v,
      return_at: v === "one_way" ? "" : f.return_at,
      return_connection: v === "one_way" ? null : f.return_connection,
      return_origin: v === "one_way" ? "" : f.return_origin,
      return_destination: v === "one_way" ? "" : f.return_destination,
      return_flight_number: v === "one_way" ? "" : f.return_flight_number,
      return_pnr: v === "one_way" ? "" : f.return_pnr,
      return_airline: v === "one_way" ? "" : f.return_airline,
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.passenger_ids.length === 0) {
      toast.error("Selecione ao menos um passageiro");
      return;
    }
    if (form.trip_type === "round_trip" && !form.return_at) {
      toast.error("Informe a data de retorno para ida e volta");
      return;
    }
    setSaving(true);
    try {
      const normConn = (c) => c ? {
        ...c,
        origin: (c.origin || "").toUpperCase(),
        destination: (c.destination || "").toUpperCase(),
        datetime_at: c.datetime_at ? new Date(c.datetime_at).toISOString() : "",
      } : null;
      const payload = {
        ...form,
        passenger_ids: form.passenger_ids,
        departure_at: new Date(form.departure_at).toISOString(),
        return_at: form.trip_type === "round_trip" && form.return_at
          ? new Date(form.return_at).toISOString()
          : null,
        price: Number(form.price) || 0,
        cost: Number(form.cost) || 0,
        ticket_url: form.ticket_url?.trim() || "",
        order_number: form.order_number?.trim() || "",
        outbound_connection: normConn(form.outbound_connection),
        return_connection: form.trip_type === "round_trip" ? normConn(form.return_connection) : null,
        return_origin: form.trip_type === "round_trip" ? (form.return_origin || "").toUpperCase() : "",
        return_destination: form.trip_type === "round_trip" ? (form.return_destination || "").toUpperCase() : "",
        return_flight_number: form.trip_type === "round_trip" ? (form.return_flight_number || "") : "",
        return_pnr: form.trip_type === "round_trip" ? (form.return_pnr || "").toUpperCase() : "",
        return_airline: form.trip_type === "round_trip" ? (form.return_airline || "") : "",
      };
      if (emission?.emission_id) {
        await api.put(`/emissions/${emission.emission_id}`, payload);
        toast.success("Emissão atualizada");
      } else {
        await api.post("/emissions", payload);
        toast.success("Emissão criada");
      }
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const q = search.toLowerCase().trim();
  const filteredClients = clients.filter((c) =>
    !q || c.full_name.toLowerCase().includes(q) || (c.cpf || "").includes(q)
  );

  const profit = (Number(form.price) || 0) - (Number(form.cost) || 0);
  const isRound = form.trip_type === "round_trip";
  const hasOutConn = !!form.outbound_connection;
  const hasRetConn = !!form.return_connection;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="emission-form-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plane className="w-5 h-5" /> {emission ? "Editar emissão" : "Nova emissão"}
          </DialogTitle>
          <DialogDescription>Dados do voo, passageiros e conexões.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Tabs value={form.trip_type} onValueChange={setTripType} className="w-full">
            <TabsList className="grid grid-cols-2 w-full" data-testid="trip-type-tabs">
              <TabsTrigger value="one_way" data-testid="trip-one-way">Só ida</TabsTrigger>
              <TabsTrigger value="round_trip" data-testid="trip-round-trip">Ida e volta</TabsTrigger>
            </TabsList>
          </Tabs>

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label>Passageiros</Label>
              <span className="text-xs text-slate-500">{form.passenger_ids.length} selecionado(s)</span>
            </div>
            <Input placeholder="Buscar por nome ou CPF" value={search} onChange={(e) => setSearch(e.target.value)}
              className="mb-2" data-testid="passengers-search" />
            <ScrollArea className="h-36 rounded-md border border-slate-200 bg-white">
              <div className="p-2 space-y-1" data-testid="passengers-list">
                {filteredClients.length === 0 ? (
                  <div className="text-slate-400 text-sm text-center py-6">Nenhum cliente encontrado.</div>
                ) : filteredClients.map((c) => {
                  const checked = form.passenger_ids.includes(c.client_id);
                  return (
                    <label key={c.client_id} htmlFor={`pax-${c.client_id}`}
                      className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-blue-50 ${checked ? "bg-blue-50" : ""}`}
                      data-testid={`passenger-row-${c.client_id}`}>
                      <Checkbox id={`pax-${c.client_id}`} checked={checked}
                        onCheckedChange={() => togglePassenger(c.client_id)} />
                      <span className="text-sm text-slate-800">{c.full_name}</span>
                      <span className="ml-auto text-xs text-slate-400">{c.cpf}</span>
                    </label>
                  );
                })}
              </div>
            </ScrollArea>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Companhia aérea</Label>
              <Select value={form.airline || ""} onValueChange={(v) => setForm((f) => ({ ...f, airline: v }))}>
                <SelectTrigger data-testid="emission-form-airline">
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {airlines.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Localizador</Label>
              <Input required value={form.pnr}
                onChange={(e) => setForm((f) => ({ ...f, pnr: e.target.value.toUpperCase() }))}
                placeholder="ABC123" data-testid="emission-form-pnr" />
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 p-3 space-y-3">
            <div className="text-sm font-semibold text-slate-800 flex items-center gap-2">
              <PlaneTakeoff className="w-4 h-4" /> Voo de ida
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Origem</Label>
                <Input required value={form.origin}
                  onChange={(e) => setForm((f) => ({ ...f, origin: e.target.value.toUpperCase() }))}
                  placeholder="GRU" data-testid="emission-form-origin" />
              </div>
              <div>
                <Label>Destino</Label>
                <Input required value={form.destination}
                  onChange={(e) => setForm((f) => ({ ...f, destination: e.target.value.toUpperCase() }))}
                  placeholder="LIS" data-testid="emission-form-destination" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Partida</Label>
                <Input type="datetime-local" required value={form.departure_at} onChange={set("departure_at")}
                  data-testid="emission-form-departure" />
              </div>
              <div>
                <Label>Nº do voo</Label>
                <Input value={form.flight_number || ""} onChange={set("flight_number")} placeholder="LA8084"
                  data-testid="emission-form-flight" />
              </div>
            </div>
            <label htmlFor="out-conn-toggle"
              className="flex items-center gap-2 cursor-pointer pt-1 text-sm">
              <Checkbox id="out-conn-toggle" checked={hasOutConn}
                onCheckedChange={(v) => setForm((f) => ({ ...f, outbound_connection: v ? (f.outbound_connection || EMPTY_CONN) : null }))}
                data-testid="outbound-connection-toggle" />
              <span className="font-medium text-slate-700">Voo com conexão</span>
            </label>
            {hasOutConn && (
              <ConnectionFields
                value={form.outbound_connection}
                onChange={(v) => setForm((f) => ({ ...f, outbound_connection: v }))}
                prefix="outbound"
              />
            )}
          </div>

          {isRound && (
            <div className="rounded-xl border border-slate-200 p-3 space-y-3"
              data-testid="return-flight-section">
              <div className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                <PlaneLanding className="w-4 h-4" /> Voo de volta
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Companhia aérea</Label>
                  <Select value={form.return_airline || ""}
                    onValueChange={(v) => setForm((f) => ({ ...f, return_airline: v }))}>
                    <SelectTrigger data-testid="emission-form-return-airline">
                      <SelectValue placeholder="Mesma da ida" />
                    </SelectTrigger>
                    <SelectContent>
                      {airlines.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Localizador</Label>
                  <div className="flex gap-1.5">
                    <Input value={form.return_pnr || ""}
                      onChange={(e) => setForm((f) => ({ ...f, return_pnr: e.target.value.toUpperCase() }))}
                      placeholder="XYZ789" data-testid="emission-form-return-pnr" />
                    <Button type="button" variant="outline" size="sm"
                      className="shrink-0 border-orange-300 text-orange-700 hover:bg-orange-50"
                      onClick={() => setForm((f) => ({ ...f, return_pnr: (f.pnr || "").toUpperCase() }))}
                      title="Copiar localizador da ida"
                      disabled={!form.pnr}
                      data-testid="emission-form-copy-pnr-return">
                      Mesmo da ida
                    </Button>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Origem</Label>
                  <Input value={form.return_origin || ""}
                    onChange={(e) => setForm((f) => ({ ...f, return_origin: e.target.value.toUpperCase() }))}
                    placeholder={form.destination || "LIS"} data-testid="emission-form-return-origin" />
                </div>
                <div>
                  <Label>Destino</Label>
                  <Input value={form.return_destination || ""}
                    onChange={(e) => setForm((f) => ({ ...f, return_destination: e.target.value.toUpperCase() }))}
                    placeholder={form.origin || "GRU"} data-testid="emission-form-return-destination" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Retorno (data/hora)</Label>
                  <Input type="datetime-local" required value={form.return_at || ""} onChange={set("return_at")}
                    data-testid="emission-form-return" />
                </div>
                <div>
                  <Label>Nº do voo</Label>
                  <Input value={form.return_flight_number || ""} onChange={set("return_flight_number")}
                    placeholder="LA8085" data-testid="emission-form-return-flight" />
                </div>
              </div>
              <label htmlFor="ret-conn-toggle"
                className="flex items-center gap-2 cursor-pointer pt-1 text-sm">
                <Checkbox id="ret-conn-toggle" checked={hasRetConn}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, return_connection: v ? (f.return_connection || EMPTY_CONN) : null }))}
                  data-testid="return-connection-toggle" />
                <span className="font-medium text-slate-700">Volta com conexão</span>
              </label>
              {hasRetConn && (
                <ConnectionFields
                  value={form.return_connection}
                  onChange={(v) => setForm((f) => ({ ...f, return_connection: v }))}
                  prefix="return"
                />
              )}
            </div>
          )}

          {form.airline === "LATAM" && (
            <div>
              <Label>Número da compra (LATAM)</Label>
              <Input value={form.order_number || ""} onChange={set("order_number")}
                placeholder="Ex.: 123456789"
                data-testid="emission-form-order-number" />
              <p className="text-xs text-slate-500 mt-1">
                Se preenchido e o link da passagem estiver vazio, o link da LATAM será gerado automaticamente.
              </p>
            </div>
          )}

          <div>
            <Label>Link da passagem (opcional)</Label>
            <Input value={form.ticket_url || ""} onChange={set("ticket_url")}
              placeholder="https://..." type="url"
              data-testid="emission-form-ticket-url" />
            <p className="text-xs text-slate-500 mt-1">
              {form.airline === "GOL"
                ? "Se vazio, o link será gerado automaticamente com PNR + origem + sobrenome."
                : form.airline === "AZUL"
                ? "Se vazio, o link será gerado automaticamente com PNR + origem."
                : form.airline === "LATAM"
                ? "Se vazio e com número da compra acima, o link será gerado automaticamente."
                : "Cole o link do bilhete/comprovante. Aparece como botão para o cliente abrir a passagem."}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Valor (R$)</Label>
              <Input type="number" step="0.01" min="0" value={form.price === 0 || form.price === "" || form.price == null ? "" : form.price}
                onChange={set("price")} placeholder="0,00"
                data-testid="emission-form-price" />
            </div>
            <div>
              <Label>Custo (R$)</Label>
              <Input type="number" step="0.01" min="0" value={form.cost === 0 || form.cost === "" || form.cost == null ? "" : form.cost}
                onChange={set("cost")} placeholder="0,00"
                data-testid="emission-form-cost" />
            </div>
          </div>
          <div className="text-xs text-slate-500">
            Lucro estimado: <b className={profit >= 0 ? "text-emerald-700" : "text-red-600"}>
              R$ {profit.toFixed(2)}
            </b>
          </div>
          <div>
            <Label>Observações</Label>
            <Textarea rows={2} value={form.notes || ""} onChange={set("notes")}
              data-testid="emission-form-notes" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={saving} className="brand-grad text-white"
              data-testid="emission-form-submit">
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
